import { Bell, CheckCircle2, AlertCircle, Info, XCircle, Clock, Database, Settings, RefreshCw, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Notification } from '../../services/alerts.service';
import type { RecentActivity } from '../../services/dashboardSummary.service';

interface NotificationsWidgetProps {
    notifications: Notification[];
    loading: boolean;
    recentActivity?: RecentActivity[];
    onMarkAsRead?: (id: string) => void;
}

type CombinedItem = {
    id: string;
    type: 'notification' | 'config_sync' | 'alert' | 'status_change';
    severity: 'success' | 'warning' | 'error' | 'info';
    title: string;
    message: string;
    timestamp: string;
    edgeId?: string;
    read?: boolean;
};

const getIcon = (type: string, severity: string) => {
    if (type === 'config_sync') {
        if (severity === 'success') return <RefreshCw className="w-4 h-4 text-success-600" />;
        if (severity === 'error') return <Settings className="w-4 h-4 text-danger-600" />;
        return <Settings className="w-4 h-4 text-warning-600" />;
    }

    switch (severity) {
        case 'success':
            return <CheckCircle2 className="w-4 h-4 text-success-600" />;
        case 'warning':
            return <AlertCircle className="w-4 h-4 text-warning-600" />;
        case 'error':
            return <XCircle className="w-4 h-4 text-danger-600" />;
        case 'info':
        default:
            return <Info className="w-4 h-4 text-info-600" />;
    }
};

const getTypeColor = (type: string, severity: string) => {
    if (type === 'config_sync') {
        switch (severity) {
            case 'success':
                return 'border-l-success-500';
            case 'error':
                return 'border-l-danger-500';
            case 'warning':
                return 'border-l-warning-500';
            default:
                return 'border-l-info-500';
        }
    }

    switch (severity) {
        case 'success':
            return 'border-l-success-500';
        case 'warning':
            return 'border-l-warning-500';
        case 'error':
            return 'border-l-danger-500';
        case 'info':
        default:
            return 'border-l-info-500';
    }
};

const formatTimestamp = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
};

const getTypeBadge = (type: string) => {
    switch (type) {
        case 'config_sync':
            return <span className="text-xs px-1.5 py-0.5 rounded bg-primary-100 text-primary-700">Config</span>;
        case 'status_change':
            return <span className="text-xs px-1.5 py-0.5 rounded bg-info-100 text-info-700">Status</span>;
        case 'alert':
            return <span className="text-xs px-1.5 py-0.5 rounded bg-warning-100 text-warning-700">Alert</span>;
        default:
            return null;
    }
};

export const NotificationsWidget = ({ notifications, loading, recentActivity, onMarkAsRead }: NotificationsWidgetProps) => {
    if (loading) {
        return (
            <div className="card animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-12 bg-gray-200 rounded"></div>
                    ))}
                </div>
            </div>
        );
    }

    // Combine notifications and recent activity into a unified timeline
    const combinedItems: CombinedItem[] = [
        ...notifications.map((n) => ({
            id: n.id,
            type: 'notification' as const,
            severity: n.type as 'success' | 'warning' | 'error' | 'info',
            title: n.title,
            message: n.message,
            timestamp: n.created_at,
            edgeId: n.edge_id || undefined,
            read: n.read === 1,
        })),
        ...(recentActivity || []).map((a) => ({
            id: a.id,
            type: a.type,
            severity: (a.severity || 'info') as 'success' | 'warning' | 'error' | 'info',
            title: a.title,
            message: a.message,
            timestamp: a.timestamp,
            edgeId: a.edgeId,
            read: true, // Activity items don't have read status
        })),
    ];

    // Sort by timestamp (newest first) and dedupe
    const sortedItems = combinedItems
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 7);

    const unreadCount = sortedItems.filter((item) => !item.read).length;

    return (
        <div className="card">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                    <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
                    {unreadCount > 0 && (
                        <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-primary-600 rounded-full">
                            {unreadCount}
                        </span>
                    )}
                </div>
                <Link
                    to="/notifications"
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                    View All →
                </Link>
            </div>

            {sortedItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-6 text-center">
                    <Bell className="w-10 h-10 text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">No recent activity</p>
                    <p className="text-xs text-gray-400 mt-1">You're all caught up!</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {sortedItems.map((item) => (
                        <div
                            key={item.id}
                            className={`group flex items-start space-x-3 p-3 rounded-lg border-l-4 ${getTypeColor(item.type, item.severity)} ${!item.read ? 'bg-gray-50' : 'bg-white'} transition-colors hover:bg-gray-50`}
                        >
                            <div className="flex-shrink-0 mt-0.5">{getIcon(item.type, item.severity)}</div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                        {item.title}
                                    </p>
                                    {getTypeBadge(item.type)}
                                    {!item.read && (
                                        <span className="w-2 h-2 bg-primary-600 rounded-full flex-shrink-0"></span>
                                    )}
                                </div>
                                <p className="text-xs text-gray-600 truncate">{item.message}</p>
                                <div className="flex items-center mt-1 text-xs text-gray-400 space-x-3">
                                    <div className="flex items-center">
                                        <Clock className="w-3 h-3 mr-1" />
                                        {formatTimestamp(item.timestamp)}
                                    </div>
                                    {item.edgeId && (
                                        <Link
                                            to={`/edges/${item.edgeId}`}
                                            className="flex items-center border-l border-gray-200 pl-3 hover:text-primary-600"
                                        >
                                            <Database className="w-3 h-3 mr-1 text-primary-500" />
                                            <span className="truncate max-w-[100px]">{item.edgeId}</span>
                                        </Link>
                                    )}
                                </div>
                            </div>
                            {/* Mark as read button */}
                            {!item.read && onMarkAsRead && (
                                <button
                                    onClick={() => onMarkAsRead(item.id)}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-200 rounded"
                                    title="Mark as read"
                                >
                                    <Check className="w-4 h-4 text-gray-500" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default NotificationsWidget;
